import { RequestPayload } from '@inertiajs/core';
import { useState } from 'react';
import { ChevronRight, Folder } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../../Shared/api';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { DonationFrequency } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../../Shared/UI/FadeIn';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { InputWrapper } from '../../../Shared/UI/InputWrapper';
import Picker, { PickerItem } from '../../../Shared/UI/Picker';
import { Select } from '../../../Shared/UI/Select';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useCurrencyInput } from '../../../Shared/useCurrencyInput';
import { useRouter } from '../../router';
import RecurringDonationsTerms from '../../Settings/DonationForms/RecurringDonationsTerms';
import { Charity } from '../../types';

export default function AddSubscriptionForm({
  donationForms,
  charity,
  onSuccess,
}: {
  donationForms: PickerItem[];
  charity: Charity;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { handleCurrencyBlur, parseCurrency } = useCurrencyInput();
  const notifier = useNotifier();

  const {
    register, handleSubmit, setError, control, setValue,
    formState: { errors, defaultValues },
  } = useForm({
    defaultValues: {
      donation_form_id: donationForms.length === 1 ? donationForms[0].id : null,
      email: '',
      first_name: '',
      last_name: '',
      amount: '',
      frequency: DonationFrequency.Monthly,
      iban: '',
    },
  });

  const [addSubscription, { loading, errors: serverErrors }] = useMutation(routes.add_subscription(), {
    onSuccess: () => {
      notifier.notify(t('backend:donation_added'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  const [formData, setFormData] = useState<RequestPayload>();

  const submit = async (data: typeof defaultValues) => {
    const formData = {
      donation_form_id: data?.donation_form_id,
      amount: data?.amount ? parseCurrency(data.amount) : null,
      frequency: data?.frequency,
      iban: data?.iban,
      first_name: data?.first_name,
      last_name: data?.last_name,
      email: data?.email,
    };

    if (!charity.has_subscriptions && !showRecurringTerms) {
      setFormData(formData);
      setShowRecurringTerms(true);
    } else {
      await addSubscription(formData);
    }
  };

  const [showRecurringTerms, setShowRecurringTerms] = useState(false);

  const acceptTermsAndContinue = () => {
    addSubscription(formData);
  };

  return (
    <>
      <FadeIn
        when={!showRecurringTerms}
        render={() => (
          <form onSubmit={handleSubmit(submit)}>
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="py-1">
                  {t('backend:add_a_recurring_donation')}
                </h3>
                <p className="text-slate-700">
                  {t('backend:add_recurring_donation_intro')}
                </p>
              </div>

              <hr />

              {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

              <InputGroup>
                <InputLabel required valid={!errors.donation_form_id} htmlFor="add_subscription_donation_form">
                  {t('backend:donation_form')}
                </InputLabel>
                <Controller
                  control={control}
                  name="donation_form_id"
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Picker
                      selected={value}
                      items={donationForms}
                      onChange={(value) => onChange(value?.id || null)}
                      onBlur={onBlur}
                      icon={<Folder />}
                      aria-invalid={!!errors.donation_form_id}
                      id="add_subscription_donation_form"
                    />
                  )}
                />
                <ErrorMessage attribute={t('backend:donation_form')} error={errors.donation_form_id} />
              </InputGroup>

              <div className="grid grid-cols-2 gap-6">
                <InputGroup>
                  <InputLabel required valid={!errors.first_name} htmlFor="add_subscription_first_name">
                    {t('backend:first_name')}
                  </InputLabel>
                  <Input
                    {...register('first_name', { required: true })}
                    id="add_subscription_first_name"
                    aria-invalid={!!errors.first_name}
                  />
                  <ErrorMessage attribute={t('backend:first_name')} error={errors.first_name }/>
                </InputGroup>

                <InputGroup>
                  <InputLabel required valid={!errors.last_name} htmlFor="add_subscription_last_name">
                    {t('backend:last_name')}
                  </InputLabel>
                  <Input
                    {...register('last_name', { required: true })}
                    id="add_subscription_last_name"
                    aria-invalid={!!errors.last_name}
                  />
                  <ErrorMessage attribute={t('backend:last_name')} error={errors.last_name }/>
                </InputGroup>
              </div>

              <InputGroup>
                <InputLabel required valid={!errors.email} htmlFor="add_subscription_email">
                  {t('backend:email_address')}
                </InputLabel>
                <Input
                  {...register('email', { required: true })} inputMode="email"
                  id="add_subscription_email"
                  aria-invalid={!!errors.email}
                />
                <ErrorMessage attribute={t('backend:email_address')} error={errors.email }/>
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.amount} htmlFor="edit_subscription_amount">
                  {t('backend:amount')}
                </InputLabel>
                <InputWrapper>
                  <span>
                  €
                  </span>
                  <Input
                    {...register('amount', { required: true })}
                    id="edit_subscription_amount"
                    className="pl-7"
                    onBlur={(event) => {
                      handleCurrencyBlur(event);
                      setValue('amount', event.target.value);
                    }}
                    aria-invalid={!!errors.amount}
                    min="0"
                  />
                </InputWrapper>
                <ErrorMessage error={errors.amount} attribute={t('backend:amount')} />
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.frequency} htmlFor="subscription_frequency">
                  {t('backend:frequency')}
                </InputLabel>
                <Select
                  {...register('frequency', { required: true })}
                  id="subscription_frequency"
                  aria-invalid={!!errors.frequency}
                >
                  <option value={DonationFrequency.Monthly}>
                    {t('backend:monthly')}
                  </option>
                  <option value={DonationFrequency.Yearly}>
                    {t('backend:yearly')}
                  </option>
                </Select>
                <ErrorMessage attribute={t('backend:frequency')} error={errors.frequency }/>
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.iban} htmlFor="subscription_iban">
                  {t('backend:iban')}
                </InputLabel>
                <Input
                  {...register('iban', { required: true })}
                  id="subscription_iban"
                  aria-invalid={!!errors.iban}
                />
                <ErrorMessage attribute={t('backend:iban')} error={errors.iban }/>
              </InputGroup>

              <hr />

              <div className="text-right">
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                >
                  {t('backend:continue')}
                  <Icon className="ml-1">
                    <ChevronRight />
                  </Icon>
                </Button>
              </div>
            </div>
          </form>
        )}
      />
      <FadeIn when={showRecurringTerms}
        render={() => (
          <RecurringDonationsTerms
            charity={charity}
            onAccept={() => acceptTermsAndContinue()}
            onRefuse={() => setShowRecurringTerms(false)}
            loading={loading}
          />
        )}
      />
    </>
  );
}
